// charts.tsx - 의존성 없는 최소 SVG/div 차트. 색은 CSS 변수만 참조한다(raw hex 금지).
// recharts 는 의도적으로 뺐다(AGENTS.md) - 새 차트도 여기에 직접 그린다.

// 두 시리즈 세로 막대. total = 전체, done = 그중 완료분(누적이 아니라 겹쳐 그린다).
export interface BarPoint {
  /** x축 라벨 (예 "6월", "3주") */
  m: string;
  total: number;
  done: number;
}

// 시계열 한 줄. alert=true 인 점은 경고색으로 강조한다(지연/이슈 표시용).
export interface LinePoint {
  date: string;
  value: number;
  alert?: boolean;
}

// onBarClick + activeIndex: 클릭 가능한 막대(주차 선택 등). 미지정 시
// 마지막 막대 하이라이트 - single-series 사용처는 done=0 으로 호출.
export const BarTrend = ({
  data,
  height = 200,
  onBarClick,
  activeIndex,
}: {
  data: BarPoint[];
  height?: number;
  onBarClick?: (i: number) => void;
  activeIndex?: number;
}) => {
  const max = Math.max(...data.map((d) => d.total), 1);
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 18, height, padding: "0 4px" }}>
      {data.map((d, i) => {
        const last = activeIndex != null ? i === activeIndex : i === data.length - 1;
        const h1 = (d.total / max) * (height - 30);
        const h2 = (d.done / max) * (height - 30);
        return (
          <div
            key={d.m}
            onClick={onBarClick ? () => onBarClick(i) : undefined}
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 8,
              cursor: onBarClick ? "pointer" : "default",
            }}
          >
            <div
              style={{
                position: "relative",
                width: "100%",
                maxWidth: 46,
                height: height - 22,
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "center",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  bottom: 0,
                  width: "100%",
                  height: h1,
                  // 브랜드 계열 고정 - 비활성 막대가 배경에 묻히지 않게 명도만 낮춘다.
                  background: last
                    ? "color-mix(in srgb, var(--accent) 58%, var(--surface))"
                    : "color-mix(in srgb, var(--accent) 30%, var(--surface))",
                  borderRadius: "4px 4px 0 0",
                }}
              />
              <div
                title={`완료 ${d.done}건`}
                style={{
                  position: "absolute",
                  bottom: 0,
                  width: "100%",
                  height: h2,
                  background: last
                    ? "var(--accent)"
                    : "color-mix(in srgb, var(--accent) 72%, var(--surface))",
                  borderRadius: "4px 4px 0 0",
                  transition: "height .4s cubic-bezier(.3,0,0,1)",
                }}
              />
              <div
                style={{
                  position: "absolute",
                  bottom: h1 + 4,
                  fontSize: 11,
                  fontWeight: 700,
                  color: last ? "var(--text-primary)" : "var(--text-tertiary)",
                  whiteSpace: "nowrap",
                }}
              >
                {d.total}
              </div>
            </div>
            <div
              style={{
                fontSize: 11.5,
                color: last ? "var(--text-primary)" : "var(--text-tertiary)",
                fontWeight: last ? 700 : 400,
              }}
            >
              {d.m}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// 비율 도넛 - value 는 0..100.
export const Donut = ({
  value,
  size = 150,
  stroke = 16,
  label,
  sub,
}: {
  value: number;
  size?: number;
  stroke?: number;
  label: string;
  sub?: string;
}) => {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const off = c * (1 - value / 100);
  return (
    <div style={{ position: "relative", width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }} aria-hidden="true">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="var(--border-subtle)"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="var(--accent)"
          strokeWidth={stroke}
          strokeDasharray={c}
          strokeDashoffset={off}
          strokeLinecap="round"
          style={{ transition: "stroke-dashoffset .6s cubic-bezier(.3,0,0,1)" }}
        />
      </svg>
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div style={{ fontFamily: "var(--font-ui)", fontWeight: 900, fontSize: 34, lineHeight: 1 }}>
          {label}
        </div>
        {sub && (
          <div style={{ fontSize: 11.5, color: "var(--text-secondary)", marginTop: 4 }}>{sub}</div>
        )}
      </div>
    </div>
  );
};

// 다중 세그먼트 도넛 (구성비). 각 세그먼트는
// strokeDasharray 로 호의 길이를, strokeDashoffset 으로 시작 위치를 잡는다.
export interface DonutSegment {
  label: string;
  value: number;
  color: string;
}
export const DonutMulti = ({
  segments,
  size = 150,
  stroke = 16,
  centerLabel,
  centerSub,
}: {
  segments: DonutSegment[];
  size?: number;
  stroke?: number;
  centerLabel: string;
  centerSub?: string;
}) => {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const total = segments.reduce((a, s) => a + s.value, 0) || 1;
  let acc = 0;
  return (
    <div style={{ position: "relative", width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }} aria-hidden="true">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="var(--border-subtle)"
          strokeWidth={stroke}
        />
        {segments.map((s) => {
          const frac = s.value / total;
          const dash = c * frac;
          const off = -c * (acc / total);
          acc += s.value;
          return (
            <circle
              key={s.label}
              cx={size / 2}
              cy={size / 2}
              r={r}
              fill="none"
              stroke={s.color}
              strokeWidth={stroke}
              strokeDasharray={`${dash} ${c - dash}`}
              strokeDashoffset={off}
            />
          );
        })}
      </svg>
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div style={{ fontFamily: "var(--font-ui)", fontWeight: 900, fontSize: 30, lineHeight: 1 }}>
          {centerLabel}
        </div>
        {centerSub && (
          <div style={{ fontSize: 11.5, color: "var(--text-secondary)", marginTop: 4 }}>
            {centerSub}
          </div>
        )}
      </div>
    </div>
  );
};

// 가로 막대 (랭킹).
export const HBars = ({
  data,
  fmt,
}: {
  data: { label: string; value: number }[];
  fmt: (n: number) => string;
}) => {
  const max = Math.max(...data.map((d) => d.value));
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {data.map((d, i) => (
        <div key={d.label} style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div
            style={{
              width: 130,
              fontSize: 13,
              color: "var(--text-secondary)",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              textAlign: "right",
            }}
          >
            {d.label}
          </div>
          <div
            style={{
              flex: 1,
              height: 22,
              background: "var(--surface-inset)",
              borderRadius: 4,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                width: `${(d.value / max) * 100}%`,
                height: "100%",
                background:
                  i === 0
                    ? "var(--accent)"
                    : "color-mix(in srgb, var(--accent) 38%, var(--surface))",
                borderRadius: 4,
                transition: "width .5s cubic-bezier(.3,0,0,1)",
              }}
            />
          </div>
          <div
            style={{
              width: 78,
              fontSize: 13,
              fontWeight: 700,
              textAlign: "right",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {fmt(d.value)}
          </div>
        </div>
      ))}
    </div>
  );
};

// 분포 스택 막대 + 범례.
export interface StatusSegment {
  key: string;
  label: string;
  color: string;
  value: number;
}
export const StatusBar = ({ segments }: { segments: StatusSegment[] }) => {
  const total = segments.reduce((a, s) => a + s.value, 0) || 1;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div
        style={{
          display: "flex",
          height: 14,
          borderRadius: 7,
          overflow: "hidden",
          background: "var(--surface-inset)",
        }}
      >
        {segments.map((s) => (
          <div
            key={s.key}
            title={`${s.label} ${s.value}건`}
            style={{ width: `${(s.value / total) * 100}%`, background: s.color }}
          />
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 20px" }}>
        {segments.map((s) => (
          <div key={s.key} style={{ display: "flex", alignItems: "center", gap: 9 }}>
            <span style={{ width: 9, height: 9, borderRadius: 2, background: s.color }} />
            <span style={{ fontSize: 13, color: "var(--text-secondary)", flex: 1 }}>{s.label}</span>
            <span style={{ fontSize: 13, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
              {s.value}건
            </span>
            <span
              style={{
                fontSize: 11.5,
                color: "var(--text-tertiary)",
                width: 40,
                textAlign: "right",
              }}
            >
              {((s.value / total) * 100).toFixed(0)}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// 스파크라인 - 추이 한 줄 + 점. 값 라벨은 넣지 않는다(폭이 좁은 카드용).
export const SeriesLine = ({
  points,
  height = 90,
  width = 320,
}: {
  points: LinePoint[];
  height?: number;
  width?: number;
}) => {
  if (!points.length) return null;
  const vals = points.map((p) => p.value);
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const pad = (max - min) * 0.2 || 1;
  const lo = min - pad;
  const hi = max + pad;
  const x = (i: number) =>
    points.length === 1 ? width / 2 : (i / (points.length - 1)) * (width - 20) + 10;
  const y = (v: number) => height - 18 - ((v - lo) / (hi - lo)) * (height - 30);
  const path = points.map((p, i) => `${i === 0 ? "M" : "L"}${x(i)},${y(p.value)}`).join(" ");
  return (
    <svg width={width} height={height} style={{ display: "block" }} aria-hidden="true">
      <path
        d={path}
        fill="none"
        stroke="var(--accent)"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {points.map((p, i) => (
        <g key={p.date}>
          <circle
            cx={x(i)}
            cy={y(p.value)}
            r={p.alert ? 4 : 3.5}
            fill={p.alert ? "var(--state-late)" : "var(--accent)"}
            stroke="var(--surface)"
            strokeWidth="2"
          />
          <text
            x={x(i)}
            y={height - 4}
            textAnchor="middle"
            fontSize="9.5"
            fill="var(--text-tertiary)"
          >
            {p.date.slice(2)}
          </text>
        </g>
      ))}
    </svg>
  );
};
